import Image from "next/image";
import { VisuallyHidden } from "radix-ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/common/components-2/dialog";
import { Icon } from "@/common/components-2/icon";
import { MigrationLink } from "@/common/components-2/migration-link";
import { isNil } from "@/common/helpers/lang";
import type { MobileNavigationProps } from "@/services/navigation/types";

function NoIconButton({ label, href }: { label: string; href: string }) {
  return (
    <a
      href={href}
      className="flex items-center justify-center rounded-full bg-dodger-blue-700 px-6 py-2 font-medium text-white"
    >
      {label}
    </a>
  );
}

function IconButton({ label, href, icon }: { label: string; href: string; icon: string }) {
  return (
    <a href={href} className="flex items-center justify-center gap-1 font-medium text-foreground">
      <Icon name={icon} className="text-2xl" />
      {label}
    </a>
  );
}

interface MobileNavbarDropdownItemProps {
  data: MobileNavigationProps["content"];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileNavbarDropdownItem({ data, open, onOpenChange }: MobileNavbarDropdownItemProps) {
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <VisuallyHidden.Root>
        <DialogTitle />
      </VisuallyHidden.Root>
      <DialogTrigger className="flex flex-col items-center justify-center gap-0.5">
        <Icon name="ph:list" className="text-2xl" />
        <span className="font-normal text-xs">Menu</span>
      </DialogTrigger>
      <DialogContent className="flex h-full flex-col overflow-y-auto bg-white">
        <VisuallyHidden.Root>
          <DialogDescription />
        </VisuallyHidden.Root>
        <DialogHeader>
          <Image src="/next-assets/logo-new.svg" alt="logo" width={140} height={24} />
        </DialogHeader>
        <ul className="mt-9 flex items-center justify-between">
          {data.buttons.map(({ label, href, icon }) => (
            <li key={label}>
              {isNil(icon) ? (
                <NoIconButton label={label} href={href} />
              ) : (
                <IconButton label={label} href={href} icon={icon} />
              )}
            </li>
          ))}
        </ul>
        <div>
          <ul>
            {data.sections.map((section, index) => (
              <li key={`${section.title}-${index}`}>
                <h3 className="mt-14 pl-3 font-medium text-muted-foreground">{section.title}</h3>
                <ul>
                  {section.items.map(({ title, description, href }) => (
                    <li key={title} className="p-3">
                      <MigrationLink href={href} onClick={() => onOpenChange(!open)}>
                        <h4 className="font-medium text-popover-foreground">{title}</h4>
                        <p className="text-muted-foreground">{description}</p>
                      </MigrationLink>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
